// src/api/discovery.ts
/**
 * Frontend API client for data source schema discovery.
 */
import { apiJson, openEventSource } from "./client";

// --- Response Types ---

export type DiscoveredColumn = {
  name: string;
  data_type: string;
  native_type: string;
  nullable: boolean;
  description?: string | null;
  sample_values?: string[] | null;
  cardinality?: number | null;
  null_percentage?: number | null;
};

export type DiscoveredTable = {
  name: string;
  full_name: string;
  schema_name?: string | null;
  dataset_id?: string | null;
  columns: DiscoveredColumn[];
  row_count?: number | null;
  size_bytes?: number | null;
  created_at?: string | null;
  modified_at?: string | null;
};

export type DiscoveryResult = {
  tables: DiscoveredTable[];
  discovered_at: string;
  schema_hash: string;
  table_count: number;
  column_count: number;
  warnings: string[];
};

export type DiscoveryStatus = {
  status: "never_run" | "pending" | "completed" | "failed";
  last_discovered_at?: string | null;
  schema_hash?: string | null;
};

// Note: apiJson auto-unwraps { data: ... } responses, so these are the direct types

// --- Input Types ---

export type TriggerDiscoveryOptions = {
  include_statistics?: boolean;
  table_limit?: number;
};

// --- API Functions ---

/**
 * Trigger schema discovery for a data source.
 * Discovers tables, columns, and optionally statistics.
 * Requires account admin role.
 */
export async function triggerDiscovery(
  dataSourceId: string,
  options?: TriggerDiscoveryOptions
): Promise<DiscoveryResult> {
  return apiJson<DiscoveryResult>(
    `/data-sources/${encodeURIComponent(dataSourceId)}/discover`,
    {
      method: "POST",
      body: JSON.stringify(options ?? {}),
    }
  );
}

/**
 * Get discovery status for a data source.
 */
export async function getDiscoveryStatus(
  dataSourceId: string
): Promise<DiscoveryStatus> {
  return apiJson<DiscoveryStatus>(
    `/data-sources/${encodeURIComponent(dataSourceId)}/discovery`
  );
}

export type CachedDiscoveryResult = {
  data: DiscoveryResult | null;
  cached: boolean;
};

/**
 * Get cached discovery results without running discovery.
 * Returns cached results from the database if available.
 */
export async function getCachedDiscovery(
  dataSourceId: string
): Promise<CachedDiscoveryResult> {
  return apiJson<CachedDiscoveryResult>(
    `/data-sources/${encodeURIComponent(dataSourceId)}/discovery/cached`,
    { unwrapData: false }
  );
}

// ============================================================================
// ASYNC DISCOVERY JOBS (New)
// ============================================================================

export interface StartDiscoveryJobRequest {
  discovery_mode?: "metadata_only" | "content_scan";
  table_names?: string[];
}

export interface DiscoveryJobResponse {
  job_id: string;
  data_source_id: string | null;
  status: "pending" | "running" | "completed" | "failed" | "cancelled";
  progress_pct: number;
  progress_message: string | null;
  tables_completed: number;
  tables_total: number;
  current_table_name: string | null;
  result_proposal_id: string | null;
  error_code: string | null;
  error_message: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export interface ProposedMember {
  value: string;
  label: string;
  synonyms: string[];
  frequency_count: number;
  frequency_pct: number;
}

export interface ProposedDimension {
  key: string;
  label: string;
  role: "time" | "categorical" | "geographic" | "attribute" | "identifier";
  description: string | null;
  synonyms: string[];
  mapped_column: string;
  members?: ProposedMember[] | null;
}

export interface ProposedMetric {
  key: string;
  label: string;
  metric_type: "aggregate" | "count_distinct" | "formula";
  aggregation: string;
  description: string | null;
  synonyms: string[];
  mapped_column: string;
  format: string;
  decimals: number;
}

export interface ProposedDataset {
  key: string;
  label: string;
  description: string | null;
  dataset_type: "fact" | "dimension";
  grain: string;
  source_table: string;
  default_time_dim_key: string | null;
  dimensions: ProposedDimension[];
  metrics: ProposedMetric[];
}

export interface ProposedJoinKey {
  source_column: string;
  target_column: string;
  suggested_logical_key: string | null;
}

export interface ProposedJoinPath {
  source_dataset_key: string;
  target_dataset_key: string;
  join_keys: ProposedJoinKey[];
  join_type: "left" | "inner";
  source_cardinality: "one" | "many";
  target_cardinality: "one" | "many";
  relationship_confidence: "high" | "medium" | "low";
  requires_review: boolean;
}

export interface DiscoveryProposalResponse {
  proposal_id: string;
  status: "needs_review" | "approved" | "rejected" | "expired";
  discovery_mode: string | null;
  proposed_package_key: string | null;
  proposed_package_label: string | null;
  proposed_datasets: ProposedDataset[];
  proposed_join_paths: ProposedJoinPath[];
  tables_analyzed: number;
  data_source_schema_hash: string | null;
  created_at: string;
  expires_at: string | null;
  applied_semantic_package_id: string | null;
}

export interface ApproveProposalResponse {
  proposal_id: string;
  status: string;
  semantic_package_id: string;
  message: string;
}

/**
 * Start an async discovery job for a data source.
 * Returns immediately with a job_id for polling.
 */
export async function startDiscoveryJob(
  dataSourceId: string,
  options: StartDiscoveryJobRequest = {}
): Promise<DiscoveryJobResponse> {
  return apiJson<DiscoveryJobResponse>(
    `/data-sources/${encodeURIComponent(dataSourceId)}/discover/start`,
    {
      method: "POST",
      body: JSON.stringify(options),
    }
  );
}

/**
 * Get the status of a discovery job.
 */
export async function getDiscoveryJobStatus(
  jobId: string
): Promise<DiscoveryJobResponse> {
  return apiJson<DiscoveryJobResponse>(
    `/discovery/jobs/${encodeURIComponent(jobId)}`
  );
}

/**
 * Cancel a discovery job.
 */
export async function cancelDiscoveryJob(jobId: string): Promise<void> {
  await apiJson(`/discovery/jobs/${encodeURIComponent(jobId)}`, {
    method: "DELETE",
  });
}

/**
 * Get a discovery proposal.
 */
export async function getDiscoveryProposal(
  proposalId: string
): Promise<DiscoveryProposalResponse> {
  return apiJson<DiscoveryProposalResponse>(
    `/discovery/proposals/${encodeURIComponent(proposalId)}`
  );
}

/**
 * Approve a discovery proposal and create the semantic package.
 */
export async function approveDiscoveryProposal(
  proposalId: string,
  idempotencyKey?: string
): Promise<ApproveProposalResponse> {
  return apiJson<ApproveProposalResponse>(
    `/discovery/proposals/${encodeURIComponent(proposalId)}/approve`,
    {
      method: "POST",
      body: JSON.stringify({ idempotency_key: idempotencyKey }),
    }
  );
}

/**
 * Reject a discovery proposal.
 */
export async function rejectDiscoveryProposal(proposalId: string): Promise<void> {
  await apiJson(
    `/discovery/proposals/${encodeURIComponent(proposalId)}/reject`,
    { method: "POST" }
  );
}

/**
 * Get active discovery jobs for the current account.
 * Returns pending, running, and recently completed (< 1 hour) jobs.
 */
export async function getActiveDiscoveryJobs(): Promise<DiscoveryJobResponse[]> {
  return apiJson<DiscoveryJobResponse[]>(`/discovery/jobs/active`);
}

// ============================================================================
// SSE Progress Stream
// ============================================================================

export interface DiscoveryProgressEvent {
  type: "progress" | "completed" | "failed" | "cancelled";
  pct?: number;
  tables_completed?: number;
  tables_total?: number;
  message?: string;
  proposal_id?: string;
  error_code?: string;
  error_message?: string;
}

/**
 * Create an EventSource for streaming discovery job progress.
 * Returns a cleanup function to close the connection.
 */
export function streamDiscoveryProgress(
  jobId: string,
  onEvent: (event: DiscoveryProgressEvent) => void,
  onError?: (error: Event) => void
): () => void {
  // Build URL using same base as API client
  const API_BASE = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "");
  const url = `${API_BASE}/discovery/jobs/${encodeURIComponent(jobId)}/stream`;

  const eventSource = openEventSource(url, { withCredentials: true });

  eventSource.addEventListener("progress", (e) => {
    try {
      const data = JSON.parse(e.data);
      onEvent({
        type: "progress",
        pct: data.pct,
        tables_completed: data.tables_completed,
        tables_total: data.tables_total,
        message: data.message,
      });
    } catch (err) {
      console.error("Failed to parse progress event:", err);
    }
  });

  eventSource.addEventListener("completed", (e) => {
    try {
      const data = JSON.parse(e.data);
      onEvent({
        type: "completed",
        proposal_id: data.proposal_id,
      });
      eventSource.close();
    } catch (err) {
      console.error("Failed to parse completed event:", err);
    }
  });

  eventSource.addEventListener("failed", (e) => {
    try {
      const data = JSON.parse(e.data);
      onEvent({
        type: "failed",
        error_code: data.error_code,
        error_message: data.error_message,
      });
      eventSource.close();
    } catch (err) {
      console.error("Failed to parse failed event:", err);
    }
  });

  eventSource.addEventListener("cancelled", () => {
    onEvent({ type: "cancelled" });
    eventSource.close();
  });

  eventSource.onerror = (error) => {
    console.error("SSE error:", error);
    onError?.(error);
    eventSource.close();
  };

  return () => {
    eventSource.close();
  };
}
